import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { EmbeddedCheckout } from '../components/EmbeddedCheckout';

export const SetupPayment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [error, setError] = useState('');
  const [pricing, setPricing] = useState(null);
  const [promotionInfo, setPromotionInfo] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  // Check if returning from successful payment
  const paymentSuccess = searchParams.get('success') === 'true' || searchParams.get('redirect_status') === 'succeeded';
  const paymentCanceled = searchParams.get('canceled') === 'true';

  const checkPaymentStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return false;
      }

      const response = await fetch('/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        navigate('/login');
        return false;
      }

      const data = await response.json();
      
      // If subscription is active, redirect to dashboard
      if (data.subscription?.status === 'active' || data.subscription?.status === 'trialing') {
        navigate('/dashboard');
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error checking status:', err);
      return false;
    }
  }, [navigate]);

  useEffect(() => {
    const init = async () => {
      await checkPaymentStatus();
      setCheckingStatus(false);
      fetchPricingInfo();
    };
    init();
  }, [checkPaymentStatus]);

  // Verify and activate subscription after successful payment
  useEffect(() => {
    if (paymentSuccess && !verifyingPayment) {
      setVerifyingPayment(true);
      let attempts = 0;
      const maxAttempts = 20; // 20 seconds max wait
      
      const verifyAndActivate = async () => {
        attempts++;
        const token = localStorage.getItem('access_token');
        
        try {
          // Call the verify-payment endpoint to activate subscription
          const verifyResponse = await fetch('/billing/verify-payment', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          const verifyData = await verifyResponse.json();
          
          if (verifyData.success && (verifyData.status === 'active' || verifyData.status === 'trialing')) {
            // Subscription activated! Navigate to dashboard
            navigate('/dashboard');
            return;
          }
          
          // If not yet active, also try checking profile
          const isActive = await checkPaymentStatus();
          if (isActive) {
            return; // checkPaymentStatus already navigated
          }
          
          // Retry if not max attempts
          if (attempts < maxAttempts) {
            setTimeout(verifyAndActivate, 1000);
          } else {
            // After max attempts, stop loading but show button
            setVerifyingPayment(false);
          }
        } catch (error) {
          console.error('Verify error:', error);
          if (attempts < maxAttempts) {
            setTimeout(verifyAndActivate, 1000);
          } else {
            setVerifyingPayment(false);
          }
        }
      };
      
      // Start verifying after a short delay
      setTimeout(verifyAndActivate, 1500);
    }
  }, [paymentSuccess, checkPaymentStatus, verifyingPayment, navigate]);

  const fetchPricingInfo = async () => {
    try {
      const promoResponse = await fetch('/billing/promotions');
      if (promoResponse.ok) {
        const promoData = await promoResponse.json();
        setPromotionInfo(promoData);
      }

      const priceResponse = await fetch('/billing/calculate-price/1');
      if (priceResponse.ok) {
        const priceData = await priceResponse.json();
        setPricing(priceData.pricing);
      }
    } catch (err) {
      console.error('Error fetching pricing:', err);
    }
  };

  const handleStartSubscription = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch('/billing/create-signup-checkout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/setup-payment?redirect_status=succeeded`,
          cancelUrl: `${window.location.origin}/setup-payment?canceled=true`,
        }),
      });

      const data = await response.json();
      
      if (data.success && data.clientSecret) {
        setClientSecret(data.clientSecret);
      } else {
        throw new Error(data.message || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToDashboard = async () => {
    setVerifyingPayment(true);
    // Try one more time to check status before navigating
    const isActive = await checkPaymentStatus();
    if (!isActive) {
      // Force navigate anyway - the dashboard will handle the status check
      navigate('/dashboard');
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-lg text-gray-600">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <>
        <SEO 
          title="Payment Successful | GOWa"
          description="Your payment was successful. Welcome to GOWa!"
        />
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-100 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
              {/* Success Header */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-10 text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">Payment Successful!</h2>
                <p className="text-green-100">Welcome to GOWa</p>
              </div>
              
              {/* Content */}
              <div className="px-8 py-8 text-center">
                <p className="text-gray-600 mb-6 text-lg">
                  Thank you for subscribing! Your account is now being activated.
                </p>
                
                {verifyingPayment ? (
                  <div className="flex flex-col items-center space-y-3 mb-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                    <span className="text-sm text-gray-500">Activating your account...</span>
                  </div>
                ) : null}
                
                <button
                  onClick={handleGoToDashboard}
                  disabled={verifyingPayment}
                  className="w-full flex justify-center items-center py-4 px-6 rounded-2xl shadow-lg text-base font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 focus:outline-none focus:ring-4 focus:ring-green-500/50 disabled:opacity-70 transition-all duration-200 transform hover:scale-[1.02]"
                >
                  {verifyingPayment ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    <>
                      Go to Dashboard
                      <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </button>
                
                <div className="mt-6 flex items-center justify-center space-x-2 text-sm text-gray-500">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>Secure payment by Stripe</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Embedded checkout view
  if (clientSecret) {
    return (
      <>
        <SEO 
          title="Complete Payment | GOWa"
          description="Complete your GOWa subscription payment."
        />
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img src="/WAGo_logo.svg" alt="GOWa Logo" className="h-14" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Complete Your Payment
              </h2>
              <p className="mt-2 text-gray-600">Enter your payment details below</p>
            </div>
            
            {/* Checkout Container */}
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
              {/* Promo Code Info Banner */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4">
                <div className="flex items-center justify-center space-x-2 text-white">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="font-medium">Have a promo code? Click "Add promotion code" below!</span>
                </div>
              </div>
              
              {/* Stripe Embedded Checkout */}
              <div className="p-6 sm:p-8">
                <EmbeddedCheckout clientSecret={clientSecret} />
              </div>
            </div>
            
            {/* Back Button */}
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setClientSecret(null);
                  setError('');
                }}
                className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to subscription details
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Main subscription page
  return (
    <>
      <SEO 
        title="Complete Your Subscription | GOWa"
        description="Complete your GOWa subscription to start using WhatsApp with GoHighLevel."
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src="/WAGo_logo.svg" alt="GOWa Logo" className="h-14" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">
              Complete Your Subscription
            </h2>
            <p className="mt-2 text-gray-600">
              One last step to activate your GOWa account
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
            {/* Promotional Banner */}
            {promotionInfo?.isPromotionalOffer && (
              <div className="bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 px-6 py-5">
                <div className="flex items-center justify-center space-x-3">
                  <span className="text-3xl">🎄</span>
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">Limited Time Offer!</div>
                    <div className="text-sm text-green-100">
                      Sign up before January 15, 2026 and lock in promotional pricing forever!
                    </div>
                  </div>
                  <span className="text-3xl">🎁</span>
                </div>
              </div>
            )}
            
            <div className="p-8">
              {/* Alerts */}
              {paymentCanceled && (
                <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 px-5 py-4 rounded-2xl flex items-start space-x-3">
                  <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Payment was canceled. Please try again when you're ready.</span>
                </div>
              )}

              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-2xl flex items-start space-x-3">
                  <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {/* Pricing Card */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Your First Subaccount</h3>
                
                <div className="bg-gradient-to-br from-gray-50 to-slate-100 rounded-2xl p-6 border border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <span className="text-gray-900 font-medium text-lg">1 Subaccount</span>
                      <p className="text-gray-500 text-sm">WhatsApp Integration</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-green-600 mb-1">7 Days Free Trial</div>
                      <span className="text-xl text-gray-400 line-through">
                        ${pricing?.breakdown?.[0]?.rate || '15'}/mo
                      </span>
                      <div className="text-xs text-gray-500 mt-1">then ${pricing?.breakdown?.[0]?.rate || '15'}/mo</div>
                    </div>
                  </div>
                  
                  {promotionInfo?.isPromotionalOffer && (
                    <div className="flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-xl">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm font-medium">Promotional pricing locked in for your account</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Features List */}
              <div className="mb-8">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">What's Included</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    'WhatsApp integration for 1 GHL subaccount',
                    'Unlimited WhatsApp messages',
                    'Easy QR code connection',
                    '7-day free trial',
                    'Cancel anytime'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3 bg-gray-50 rounded-xl px-4 py-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleStartSubscription}
                disabled={loading}
                className="w-full flex justify-center items-center py-4 px-6 rounded-2xl shadow-lg text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    Start Free Trial
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>

              {/* Security Badge */}
              <div className="mt-6 flex items-center justify-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>Secure checkout</span>
                </div>
                <span className="text-gray-300">|</span>
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                  </svg>
                  <span>Powered by Stripe</span>
                </div>
              </div>
            </div>
          </div>

          {/* Help Link */}
          <div className="mt-6 text-center">
            <a href="mailto:info@gowa.app" className="text-gray-500 hover:text-gray-700 text-sm transition-colors">
              Need help? Contact support
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default SetupPayment;
